import { EventEmitter } from 'eventemitter3';
import { GeneralException, WalletException } from '@injectivelabs/exceptions';
import { Wallet, isEvmWallet, isCosmosWallet, } from '@injectivelabs/wallet-base';
import { WalletStrategyEmitterEventType } from '../broadcaster/types.js';
const getInitialWallet = (args) => {
    if (args.wallet) {
        return args.wallet;
    }
    const keys = Object.keys(args.strategies || {});
    if (keys.length === 0) {
        return Wallet.Metamask;
    }
    if (keys.includes(Wallet.Metamask) && args.evmOptions) {
        return Wallet.Metamask;
    }
    if (keys.includes(Wallet.Keplr) && !args.evmOptions) {
        return Wallet.Keplr;
    }
    return keys[0];
};
export default class BaseWalletStrategy {
    strategies;
    wallet;
    args;
    metadata;
    wallets;
    emitter;
    on;
    off;
    emit;
    constructor(args) {
        this.args = args;
        this.strategies = args.strategies;
        this.wallet = getInitialWallet(args);
        this.metadata = args.metadata;
        this.emitter = new EventEmitter();
        this.on = this.emitter.on.bind(this.emitter);
        this.off = this.emitter.off.bind(this.emitter);
        this.emit = this.emitter.emit.bind(this.emitter);
    }
    getWallet() {
        return this.wallet;
    }
    setWallet(wallet) {
        this.wallet = wallet;
        this.getStrategy();
    }
    setMetadata(metadata) {
        console.log('Setting metadata', metadata);
        this.metadata = metadata;
        this.getStrategy().setMetadata?.(metadata);
    }
    getStrategy() {
        if (!this.strategies[this.wallet]) {
            throw new GeneralException(new Error(`Wallet ${this.wallet} is not enabled/available!`));
        }
        return this.strategies[this.wallet];
    }
    getAddresses(args) {
        return this.getStrategy().getAddresses(args);
    }
    getWalletDeviceType() {
        return this.getStrategy().getWalletDeviceType();
    }
    getPubKey(address) {
        return this.getStrategy().getPubKey(address);
    }
    enable(args) {
        return this.getStrategy().enable(args);
    }
    async enableAndGetAddresses(args) {
        await this.getStrategy().enable(args);
        return this.getStrategy().getAddresses(args);
    }
    getEthereumChainId() {
        return this.getStrategy().getEthereumChainId();
    }
    async getEvmTransactionReceipt(txHash, evmChainId) {
        return this.getStrategy().getEvmTransactionReceipt(txHash, evmChainId);
    }
    async getSessionOrConfirm(address) {
        return this.getStrategy().getSessionOrConfirm(address);
    }
    async getWalletClient() {
        if (this.getStrategy()?.getWalletClient) {
            const result = (this.getStrategy()?.getWalletClient)?.();
            if (result) {
                return result;
            }
        }
        throw new WalletException(new Error('Wallet client not found. Please check your wallet strategy.'));
    }
    async sendTransaction(tx, options) {
        return this.getStrategy().sendTransaction(tx, options);
    }
    async sendEvmTransaction(tx /* TODO */, options) {
        return this.getStrategy().sendEvmTransaction(tx, options);
    }
    async signEip712TypedData(eip712TypedData, address, options = {}) {
        if (isCosmosWallet(this.wallet)) {
            throw new WalletException(new Error(`You can't sign Ethereum Transaction using ${this.wallet}`));
        }
        /** Phantom wallet needs enabling before signing */
        if (this.wallet === Wallet.Phantom) {
            await this.enable();
        }
        this.emit(WalletStrategyEmitterEventType.TransactionSignStart);
        const response = await this.getStrategy().signEip712TypedData(eip712TypedData, address, options);
        this.emit(WalletStrategyEmitterEventType.TransactionSigned);
        return response;
    }
    async signAminoCosmosTransaction(transaction) {
        if (isEvmWallet(this.wallet)) {
            throw new WalletException(new Error(`You can't sign Cosmos Transaction using ${this.wallet}`));
        }
        this.emit(WalletStrategyEmitterEventType.TransactionSignStart);
        const response = await this.getStrategy().signAminoCosmosTransaction(transaction);
        this.emit(WalletStrategyEmitterEventType.TransactionSigned);
        return response;
    }
    async signCosmosTransaction(transaction) {
        if (isEvmWallet(this.wallet)) {
            throw new WalletException(new Error(`You can't sign Cosmos Transaction using ${this.wallet}`));
        }
        this.emit(WalletStrategyEmitterEventType.TransactionSignStart);
        const response = await this.getStrategy().signCosmosTransaction(transaction);
        this.emit(WalletStrategyEmitterEventType.TransactionSigned);
        return response;
    }
    async signArbitrary(signer, data) {
        if (this.getStrategy().signArbitrary) {
            this.emit(WalletStrategyEmitterEventType.TransactionSignStart);
            const response = await this.getStrategy().signArbitrary(signer, data);
            this.emit(WalletStrategyEmitterEventType.TransactionSigned);
            return response;
        }
    }
    async onAccountChange(callback) {
        if (this.getStrategy().onAccountChange) {
            return this.getStrategy().onAccountChange(callback);
        }
    }
    async onChainIdChange(callback) {
        if (this.getStrategy().onChainIdChange) {
            return this.getStrategy().onChainIdChange(callback);
        }
    }
    async disconnect() {
        if (this.getStrategy().disconnect) {
            await this.getStrategy().disconnect();
            this.emit(WalletStrategyEmitterEventType.WalletStrategyDisconnect);
        }
    }
    getCosmosWallet(chainId) {
        const strategy = this.getStrategy();
        if (strategy.getCosmosWallet == undefined) {
            throw new WalletException(new Error(`This method is not available for ${this.getWallet()} wallet`));
        }
        return strategy.getCosmosWallet(chainId);
    }
    async getEip1193Provider() {
        if (this.getStrategy().getEip1193Provider) {
            return this.getStrategy().getEip1193Provider();
        }
        throw new WalletException(new Error('EIP1193 provider not found. Please check your wallet strategy.'));
    }
    async getOfflineSigner(chainId) {
        if (this.getStrategy().getOfflineSigner) {
            return this.getStrategy().getOfflineSigner(chainId);
        }
        throw new WalletException(new Error('Offline signer not found. Please check your wallet strategy.'));
    }
}
